/**
 * Owner: Masa Bayu
 * File: batch03-evidence.ts
 * Date:Time: 2026-09-19 23:41
 * Description: Batch 3 evidence packs — CJK chinese · japanese · korean · READY.
 */
import { QX_BATCH_03_EVIDENCE_AREAS, QX_BATCH_03_MODEL_VERSION, QX_GL_CERT_GLOBAL_BATCH_03_ID, } from './batch03-codes';
import { QX_BATCH_03_CHINESE_SAMPLE_LANGUAGES, QX_BATCH_03_JAPANESE_SAMPLE_LANGUAGES, QX_BATCH_03_KOREAN_SAMPLE_LANGUAGES, QX_BATCH_03_TEXT_SAMPLES, fixturesForBatch03Family, listBatch03FixtureIds, } from './batch03-fixtures';
import { createQxAccessibilityLanguageSemantics } from './a11y-semantics';
import { allEvidenceTrue, evaluateEvidencePath, } from './certification-evidence';
import { createQxFamilyCertificationRow, supportClaimForRow, } from './certification-row';
import { getCoverageScriptDirectionRow } from './coverage-script-matrix';
import { resolveDirectionHint } from './direction-resolve';
import { QxLanguageError } from './errors';
import { createQxLanguageIdentity } from './identity';
import { assertQxpLanguageMetadataFromQxwOnly, createQxLanguageMetadataBinding, preservePublicationLanguageMetadata, } from './metadata-placement';
import { createQxMultilingualContentModel, applyOverrideWithoutChangingContentId, } from './multilingual-content';
import { roundTripQxMultilingualContentModel, serializeQxMultilingualContentModel, } from './multilingual-serialize';
import { parseQxPublicationLanguageMetadata, serializeQxPublicationLanguageMetadata, } from './metadata-serialize';
import { createQxPublicationLanguageMetadata } from './publication-metadata';
import { resolveLanguageScriptRelation } from './script-relation';
import { createQxTextProcessingProfile, prepareSearchText, } from './search-prep';
import { normalizeTextForSearch } from './text-normalize';
import { resolveTypographyForScript } from './typography-resolve';
import { resolveWritingModeHint } from './writing-mode-resolve';
const FAMILY_CFG = Object.freeze({
    chinese: Object.freeze({
        scriptIds: Object.freeze(['Hans', 'Hant']),
        primary: 'zh-Hans',
        secondary: 'zh-Hant',
        samples: QX_BATCH_03_CHINESE_SAMPLE_LANGUAGES,
        sampleText: QX_BATCH_03_TEXT_SAMPLES['zh-Hans'],
        contentId: 'doc-chinese-batch03-01',
        primaryScriptId: 'Hans',
    }),
    japanese: Object.freeze({
        scriptIds: Object.freeze(['Jpan']),
        primary: 'ja',
        secondary: 'ja',
        samples: QX_BATCH_03_JAPANESE_SAMPLE_LANGUAGES,
        sampleText: QX_BATCH_03_TEXT_SAMPLES.ja,
        contentId: 'doc-japanese-batch03-01',
        primaryScriptId: 'Jpan',
    }),
    korean: Object.freeze({
        scriptIds: Object.freeze(['Kore']),
        primary: 'ko',
        secondary: 'ko',
        samples: QX_BATCH_03_KOREAN_SAMPLE_LANGUAGES,
        sampleText: QX_BATCH_03_TEXT_SAMPLES.ko,
        contentId: 'doc-korean-batch03-01',
        primaryScriptId: 'Kore',
    }),
});
function allAreasTrue(areas) {
    for (const key of QX_BATCH_03_EVIDENCE_AREAS) {
        if (!areas[key]) {
            return false;
        }
    }
    return true;
}
/**
 * Build one Batch 3 family evidence pack (CJK-aware).
 * Does not stamp Owner Accept or allow Supported.
 */
export function buildBatch03FamilyEvidenceBundle(familyId) {
    const cfg = FAMILY_CFG[familyId];
    const areas = {};
    const fixtures = fixturesForBatch03Family(familyId);
    const matrixRow = getCoverageScriptDirectionRow(familyId);
    const primary = createQxLanguageIdentity({
        languageId: cfg.primary,
        plane: 'publication',
    });
    const secondary = createQxLanguageIdentity({
        languageId: cfg.secondary,
        plane: 'publication',
    });
    areas.identity =
        primary.languageId === cfg.primary &&
            secondary.languageId === cfg.secondary &&
            cfg.samples.includes(cfg.primary);
    const scriptPrimary = resolveLanguageScriptRelation(cfg.primary);
    const scriptSecondary = resolveLanguageScriptRelation(cfg.secondary);
    areas.script =
        scriptPrimary.resolvedScriptId === cfg.primaryScriptId &&
            (familyId !== 'chinese' || scriptSecondary.resolvedScriptId === 'Hant') &&
            cfg.scriptIds.every((id) => matrixRow.primaryScriptIds.includes(id));
    const dir = resolveDirectionHint(cfg.primary);
    const dirOverride = resolveDirectionHint(cfg.primary, 'ltr');
    areas.direction =
        dir.hintedDirection === 'ltr' && dirOverride.override === 'ltr';
    const contentLanguages = [...cfg.samples];
    const overrides = familyId === 'chinese'
        ? [
            {
                scope: 'inline',
                spanId: 'quote-zh-Hant',
                languageId: 'zh-Hant',
            },
        ]
        : [];
    const content = createQxMultilingualContentModel({
        contentId: cfg.contentId,
        primaryLanguageId: cfg.primary,
        contentLanguages,
        overrides,
    });
    areas.content =
        content.primaryLanguageId === cfg.primary &&
            content.contentLanguages.includes(cfg.primary);
    const pubMeta = createQxPublicationLanguageMetadata({
        primaryLanguageId: cfg.primary,
        contentLanguages,
    });
    const qxwBinding = createQxLanguageMetadataBinding({
        placement: 'qxw_document_root',
        primaryLanguageId: pubMeta.primaryLanguageId,
        defaultScriptId: pubMeta.defaultScriptId,
        defaultDirection: pubMeta.defaultDirection,
        defaultWritingMode: pubMeta.defaultWritingMode,
        contentLanguages: pubMeta.contentLanguages,
    });
    const preserved = preservePublicationLanguageMetadata(qxwBinding.metadata);
    areas.qxw =
        preserved.primaryLanguageId === cfg.primary &&
            preserved.defaultScriptId === cfg.primaryScriptId &&
            preserved.defaultDirection === 'ltr';
    let qxpOk = false;
    try {
        assertQxpLanguageMetadataFromQxwOnly(true);
        const qxp = createQxLanguageMetadataBinding({
            placement: 'qxp_artifact',
            primaryLanguageId: cfg.primary,
            contentLanguages,
        });
        qxpOk =
            qxp.preserveThroughPublish === true &&
                qxp.metadata.defaultDirection === 'ltr';
    }
    catch {
        qxpOk = false;
    }
    areas.qxp = qxpOk;
    const typo = resolveTypographyForScript({
        role: 'body',
        languageId: cfg.primary,
    });
    const heading = resolveTypographyForScript({
        role: 'heading',
        languageId: cfg.primary,
    });
    const typoSecondary = familyId === 'chinese'
        ? resolveTypographyForScript({ role: 'body', languageId: 'zh-Hant' })
        : typo;
    areas.reader =
        typo.scriptId === cfg.primaryScriptId &&
            heading.scriptId === cfg.primaryScriptId &&
            typo.primaryFontFamilyId.startsWith('qx-cjk') &&
            typo.fallbackChain.length > 0 &&
            (familyId !== 'chinese' || typoSecondary.scriptId === 'Hant');
    const profile = createQxTextProcessingProfile({ languageId: cfg.primary });
    const prep = prepareSearchText(profile, cfg.sampleText);
    const nfc = normalizeTextForSearch(QX_BATCH_03_TEXT_SAMPLES.composed, 'NFC');
    const forbidWhitespaceDefault = !matrixRow.whitespaceTokenizationSafe
        ? profile.segmentStrategy === 'script_aware'
        : true;
    areas.search =
        profile.analyzerFamily === 'cjk_script' &&
            profile.segmentStrategy === 'script_aware' &&
            prep.segmentation.strategy === 'script_aware' &&
            prep.segmentation.engineRequired === true &&
            forbidWhitespaceDefault &&
            nfc.normalized === 'café' &&
            nfc.original === QX_BATCH_03_TEXT_SAMPLES.composed;
    const wm = resolveWritingModeHint(cfg.primary);
    const wmVertical = resolveWritingModeHint(cfg.primary, 'vertical-rl');
    areas.navigation =
        wm.hintedWritingMode === 'horizontal-tb' &&
            wm.allowedWritingModes.includes('vertical-rl') &&
            wmVertical.override === 'vertical-rl' &&
            heading.role === 'heading';
    areas.annotation = nfc.original !== nfc.normalized && nfc.original.length > 0;
    const a11y = createQxAccessibilityLanguageSemantics({
        contentLanguageId: cfg.primary,
        uiAnnouncementLanguageId: 'en',
        spans: familyId === 'chinese'
            ? [
                {
                    spanId: 'quote-zh-Hant',
                    languageId: 'zh-Hant',
                    boundary: 'inline',
                },
            ]
            : [],
    });
    areas.accessibility =
        a11y.contentLangTag === cfg.primary &&
            a11y.direction === 'ltr' &&
            a11y.readingOrderPreserved === true &&
            (familyId !== 'chinese' || a11y.spanSemantics[0]?.langTag === 'zh-Hant');
    const metaRt = parseQxPublicationLanguageMetadata(serializeQxPublicationLanguageMetadata(pubMeta));
    const contentRt = roundTripQxMultilingualContentModel(content);
    const json = serializeQxMultilingualContentModel(content);
    areas.serialization =
        metaRt.primaryLanguageId === cfg.primary &&
            metaRt.defaultDirection === 'ltr' &&
            contentRt.contentId === cfg.contentId &&
            typeof json.modelVersion === 'string';
    const overrideLang = familyId === 'chinese' ? 'zh-Hant' : cfg.primary;
    const next = applyOverrideWithoutChangingContentId(content, {
        scope: 'block',
        spanId: `note-${overrideLang}`,
        languageId: overrideLang,
    }, cfg.contentId);
    areas.regression =
        next.contentId === content.contentId &&
            next.overrides.length === content.overrides.length + 1;
    const provenance = fixtures.map((f) => Object.freeze({ fixtureId: f.id, area: f.area }));
    const areaSet = new Set(QX_BATCH_03_EVIDENCE_AREAS);
    areas.provenance =
        provenance.length === QX_BATCH_03_EVIDENCE_AREAS.length &&
            provenance.every((p) => areaSet.has(p.area));
    const frozenAreas = Object.freeze({ ...areas });
    const pathEvidence = allEvidenceTrue();
    const pathEval = evaluateEvidencePath(pathEvidence);
    const areasPass = allAreasTrue(frozenAreas);
    const row = createQxFamilyCertificationRow({
        familyId,
        scriptIds: [...cfg.scriptIds],
        sampleLanguageIds: [...cfg.samples],
        directions: ['ltr'],
        level: 'experimental',
        evidence: pathEvidence,
        evidenceRefs: listBatch03FixtureIds(familyId),
        gaps: ['awaiting_owner_accept_for_certified', 'batch_open_not_supported'],
        ownerAcceptedAt: null,
    });
    const claimStatus = supportClaimForRow(row);
    if (claimStatus === 'supported') {
        throw new QxLanguageError('INVALID_INPUT', `${familyId} must not claim Supported before Owner Accept`);
    }
    const ready = areasPass && pathEval.status === 'pass';
    return Object.freeze({
        modelVersion: QX_BATCH_03_MODEL_VERSION,
        batchId: QX_GL_CERT_GLOBAL_BATCH_03_ID,
        familyId,
        scriptIds: cfg.scriptIds,
        areas: frozenAreas,
        pathEvidence,
        pathStatus: pathEval.status,
        fixtures: listBatch03FixtureIds(familyId),
        provenance: Object.freeze(provenance),
        row,
        supportedClaimAllowed: false,
        claimStatus,
        readyForOwnerAccept: ready,
        automatedTestsPass: ready,
        manualQaChecklistPackaged: true,
        a11ySpotCheckPass: areas.accessibility,
        notes: ready
            ? `${familyId} Batch 3 CJK evidence READY — not Supported (awaiting Owner Accept)`
            : `${familyId} Batch 3 evidence incomplete`,
    });
}
export function buildBatch03EvidenceReport() {
    const chinese = buildBatch03FamilyEvidenceBundle('chinese');
    const japanese = buildBatch03FamilyEvidenceBundle('japanese');
    const korean = buildBatch03FamilyEvidenceBundle('korean');
    const ready = chinese.readyForOwnerAccept &&
        japanese.readyForOwnerAccept &&
        korean.readyForOwnerAccept;
    return Object.freeze({
        batchId: QX_GL_CERT_GLOBAL_BATCH_03_ID,
        modelVersion: QX_BATCH_03_MODEL_VERSION,
        chinese,
        japanese,
        korean,
        readyForOwnerAccept: ready,
        batchOpenImpliesSupported: false,
        supportedFamilyIdsStill: Object.freeze([
            'latin-core',
            'arabic',
            'hebrew',
            'cyrillic',
            'greek',
        ]),
    });
}
//# sourceMappingURL=batch03-evidence.js.map